<template>
  <section class="leadership-home">
    <header class="leadership-intro">
      <h2>Leadership</h2>
      <p>Deine Bereiche für Entwicklung, Wissen und Austausch.</p>
    </header>

    <nav v-if="hasLeadershipAccess || canReadAudits" class="leadership-groups" aria-label="Leadership-Bereiche">
      <section v-for="group in visibleGroups" :key="group.id" class="leadership-group" :aria-labelledby="'leadership-' + group.id">
        <h3 :id="'leadership-' + group.id">{{ group.title }}</h3>
        <div class="group-links">
          <router-link
            v-for="area in group.areas"
            :key="area.path"
            :to="area.path"
            class="area-link"
            :style="{ '--accent': area.color, '--accent-bg': area.background }">
            <span class="area-icon" aria-hidden="true"><v-icon>{{ area.icon }}</v-icon></span>
            <div class="area-copy">
              <h4>{{ area.title }}</h4>
              <p>{{ area.description }}</p>
              <span v-if="area.badge" class="area-note">{{ area.badge }}</span>
            </div>
            <v-icon class="area-arrow" aria-hidden="true">mdi-chevron-right</v-icon>
          </router-link>
          <StudioAuditCallings v-if="group.id === 'daily' && canReadAudits" :class="{ 'audit-entry-divider': hasLeadershipAccess }" />
        </div>
      </section>
    </nav>
  </section>
</template>

<script>
import userService from "@/services/userService"
import StudioAuditCallings from "../controlling/StudioAuditCallings.vue"
export default {
  name: "LeadershipHome",
  components: { StudioAuditCallings },
  computed: {
    visibleGroups() {
      return this.hasLeadershipAccess ? this.groups : this.groups.filter(group => group.id === "daily").map(group => ({ ...group, areas: [] }))
    },
    canReadAudits() { return userService.hasRight("aka_teacher", "aka_allAudits") },
    hasLeadershipAccess() { return userService.hasRight("aka_leadership", "aka_leadership_manage") },
  },
  data() {
    return {
      groups: [
        {
          id: "daily",
          title: "Organisation & Wissen",
          areas: [
            {
              title: "Dokumente",
              description: "Leitfäden und Unterlagen nachschlagen.",
              icon: "mdi-folder-key-outline",
              path: "/akademie/leadership/docs",
              color: "#f9a8d4", background: "rgba(236, 72, 153, 0.13)",
            },
          ],
        },
        {
          id: "learning",
          title: "Lernen & Entwicklung",
          areas: [
            {
              title: "Coaching",
              description: "Lernmodule und Übungen durcharbeiten.",
              icon: "mdi-school-outline",
              path: "/akademie/leadership/coaching",
              color: "#93c5fd", background: "rgba(59, 130, 246, 0.13)",
            },
            {
              title: "Coaching 1:1",
              description: "Persönlichen Termin oder Extra-Session anfragen.",
              icon: "mdi-account-supervisor-circle-outline",
              path: "/akademie/leadership/coaching-1to1",
              color: "#fcd34d", background: "rgba(245, 158, 11, 0.13)",
            },
          ],
        },
        {
          id: "exchange",
          title: "Austausch & Ideen",
          areas: [
            {
              title: "Connect",
              description: "Fragen und Erfahrungen im Chat teilen.",
              icon: "mdi-forum-outline",
              path: "/akademie/leadership/connect",
              color: "#7ee5a6", background: "rgba(0, 150, 64, 0.13)",
            },
            {
              title: "Creative",
              description: "Ideen und Führungsimpulse austauschen.",
              icon: "mdi-lightbulb-group-outline",
              path: "/akademie/leadership/creative",
              color: "#c4b5fd", background: "rgba(139, 92, 246, 0.13)",
              badge: "Nach Modulabschluss",
            },
          ],
        },
      ],
    }
  },
}
</script>

<style scoped>
.audit-entry-divider { border-top: 1px solid rgba(255, 255, 255, 0.08); }
.leadership-home {
  color: #f4f4f5;
  font-size: 1rem;
}

.leadership-intro {
  margin: 0.5rem 0 1.25rem;
}

.leadership-intro h2 {
  margin: 0 0 0.375rem;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.3;
}

.leadership-intro p {
  margin: 0;
  color: #c7c7cb;
  font-size: 1rem;
  line-height: 1.5;
}

.leadership-groups {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.leadership-group {
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.leadership-group > h3 {
  margin: 0 0 0.5rem;
  padding: 0.25rem 0.5rem 0.625rem;
  color: #c7c7cb;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
}

.group-links {
  display: grid;
}

.area-link {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.75rem;
  padding: 1rem 0.5rem;
  border-radius: 0.5rem;
  color: inherit;
  text-decoration: none;
}

.area-link + .area-link {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.area-link:hover,
.area-link:focus {
  background: rgba(255, 255, 255, 0.045);
  text-decoration: none;
}

.area-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.area-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: var(--accent);
  background: var(--accent-bg);
}

.area-icon .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.area-copy {
  min-width: 0;
  overflow-wrap: anywhere;
}

.area-copy h4 {
  margin: 0 0 0.25rem;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5;
}

.area-copy p {
  margin: 0;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.5;
}

.area-note {
  display: block;
  margin-top: 0.375rem;
  color: var(--accent);
  font-size: 0.75rem;
  line-height: 1.5;
}

.area-arrow {
  margin-top: 0.25rem;
  color: #b8b8bc;
  font-size: 1.25rem;
}

@media (max-width: 68rem) {
  .leadership-groups {
    grid-template-columns: minmax(0, 1fr);
  }

  .group-links {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .area-link + .area-link {
    border-top: 0;
  }
}

@media (max-width: 43rem) {
  .leadership-groups {
    gap: 0.75rem;
  }

  .leadership-group {
    padding: 0.75rem;
  }

  .group-links {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  .area-link + .area-link {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
}
</style>
